import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, ChevronRight, MapPin, Clock } from "lucide-react";
import { getAreaBySlug } from "@/data/areas";
import { getServices } from "@/data/services";
import { SITE, imageProxy } from "@/data/site";
import { getAreaFaqsGeneral } from "@/data/faqs";
import { useLang } from "@/lib/i18n";
import QuoteForm from "@/components/site/QuoteForm";
import FloridaMap from "@/components/site/ServiceAreaMap";
import FAQ from "@/components/site/FAQ";
import ReadyCTA from "@/components/site/ReadyCTA";

export default function AreaDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const area = getAreaBySlug(slug, lang);
  if (!area) return <Navigate to="/service-areas" replace />;

  const SERVICES = getServices(lang);
  const FAQS = getAreaFaqsGeneral(lang);
  const heroImg = area.image.startsWith("http")
    ? imageProxy(area.image, { w: 1600, q: 78 })
    : area.image;

  return (
    <>
      <section data-testid="area-hero" className="relative">
        <div className="absolute inset-0">
          <img
            src={heroImg}
            alt={area.name}
            className="w-full h-full object-cover"
            style={{ filter: "blur(2px) brightness(0.78) saturate(1.05)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy-dark/82 via-brand-navy-dark/65 to-brand-navy-dark/35" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,191,0,0.15)_0%,transparent_55%)]" />
        </div>
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/70 to-transparent" />
        <div className="relative container-max py-20 sm:py-28 text-white">
          <nav className="text-xs text-white/80 flex items-center gap-1.5 mb-4">
            <Link to="/" className="hover:text-white">{t.nav.home}</Link>
            <ChevronRight size={12} />
            <Link to="/service-areas" className="hover:text-white">{t.nav.areas}</Link>
            <ChevronRight size={12} />
            <span className="text-white font-medium">{area.name}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-yellow text-slate-900 px-3.5 py-1.5 text-xs font-bold tracking-wide shadow-lg shadow-yellow-300/20">
            <MapPin size={14} />
            {t.common.southFlorida} · {area.name}
          </div>
          <h1
            className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl"
            style={{ textShadow: "0 2px 18px rgba(0,0,0,0.55)" }}
          >
            {lang === "es" ? `Plomero en ${area.name}.` : `Plumber in ${area.name}.`}
          </h1>
          <p
            className="mt-5 text-white/95 text-lg max-w-2xl leading-relaxed"
            style={{ textShadow: "0 1px 8px rgba(0,0,0,0.5)" }}
          >
            {area.intro}
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a data-testid="area-call-cta" href={`tel:${SITE.primaryPhone}`}
              className="inline-flex items-center justify-center gap-2 bg-brand-yellow hover:bg-brand-yellow-dark text-slate-900 font-bold py-3.5 px-5 rounded-xl whitespace-nowrap shadow-lg shadow-yellow-300/30">
              <Phone size={18} />
              <span>{t.cta.call} {SITE.primaryPhone}</span>
            </a>
            <a href="#quote" data-testid="area-quote-cta"
              className="inline-flex items-center justify-center font-bold py-3.5 px-5 rounded-xl border-2 border-white/50 bg-white/10 backdrop-blur-md text-white hover:bg-brand-yellow hover:text-slate-900 hover:border-brand-yellow transition-colors whitespace-nowrap">
              {t.cta.freeEstimate}
            </a>
          </div>
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-white/95 bg-white/10 backdrop-blur-md rounded-full px-3.5 py-1.5">
            <Clock size={16} className="text-brand-yellow" />
            {area.responseTime}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-max grid lg:grid-cols-[1.2fr_1fr] gap-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow-dark">
              {lang === "es" ? "Conocimiento local" : "Local knowledge"}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-2 leading-tight">
              {lang === "es" ? `Por qué los clientes de ${area.name} nos llaman.` : `Why ${area.name} customers call us.`}
            </h2>
            <p className="mt-5 text-slate-700 text-lg leading-relaxed">{area.localCopy}</p>
            <p className="mt-5 text-slate-700 text-lg leading-relaxed">{area.notes}</p>

            <h3 className="mt-12 font-heading text-2xl font-bold text-slate-900">
              {lang === "es" ? "Ciudades y barrios que cubrimos" : "Cities & neighborhoods we cover"}
            </h3>
            <div className="mt-5 flex flex-wrap gap-2">
              {area.cities.map((c) => (
                <span key={c}
                  className="inline-flex items-center gap-1.5 bg-brand-yellow/15 text-slate-800 px-3 py-1.5 rounded-full text-sm border border-brand-yellow/40">
                  <MapPin size={12} className="text-brand-yellow-dark" />
                  {c}
                </span>
              ))}
            </div>

            <h3 className="mt-12 font-heading text-2xl font-bold text-slate-900">
              {lang === "es" ? `Servicios en ${area.name}` : `Services available in ${area.name}`}
            </h3>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {SERVICES.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`area-service-${s.slug}`}
                  className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-3 hover:border-brand-yellow hover:shadow-md transition-all">
                  <span className="font-medium text-slate-800 text-sm">{s.name}</span>
                  <ChevronRight size={16} className="text-slate-400" />
                </Link>
              ))}
            </div>
          </div>

          <div id="quote" className="lg:sticky lg:top-28 self-start">
            <QuoteForm sourcePage={`area:${area.slug}`} />
          </div>
        </div>
      </section>

      <section className="section-padding bg-[radial-gradient(ellipse_at_top,#EFF6FF_0%,#F8FAFC_50%,#F1F5F9_100%)]">
        <div className="container-max">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow-dark">
              {lang === "es" ? "Dónde estamos" : "Where we are"}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-2 leading-tight">
              {area.name.replace(/ County$/, "")} {lang === "es" ? "en el mapa." : "on the map."}
            </h2>
          </div>
          <div className="mt-8">
            <FloridaMap highlightSlug={area.slug} compact />
          </div>
        </div>
      </section>

      <FAQ items={FAQS} eyebrow={`${area.name} · FAQ`} />

      <ReadyCTA />
    </>
  );
}
